import { Method, RequestParams } from '@_api/_base';

window.setState =
  window.setState ||
  function (key: string, value: string) {
    window.localStorage.setItem(key, value);
    window.dispatchEvent(new StorageEvent('storage', { key, newValue: value }));
  };

Element.prototype.observeResize =
  Element.prototype.observeResize ||
  function (this: Element, callback: () => void) {
    if ('ResizeObserver' in window) {
      // create an Observer instance
      const resizeObserver = new ResizeObserver(callback);

      // start observing a DOM node
      resizeObserver.observe(this);
    } else {
      let lastRect = this.getBoundingClientRect();

      setInterval(() => {
        const rect = this.getBoundingClientRect();

        if (Math.floor(rect.width) != Math.floor(lastRect.width) || Math.floor(rect.height) != Math.floor(lastRect.height)) {
          lastRect = rect;
          callback();
        }
      }, 500);
    }
  };

HTMLElement.prototype.dragScroll =
  HTMLElement.prototype.dragScroll ||
  function (this: HTMLElement) {
    const dragStart = () => {
      this.dataset.dragging = 'true';
    };
    const dragEnd = () => {
      this.dataset.dragging = 'false';
    };
    const drag = (ev: MouseEvent) => {
      if (this.dataset.dragging === 'true') this.scrollLeft -= ev.movementX;
    };

    this.addEventListener('mousedown', dragStart);
    this.addEventListener('mouseup', dragEnd);
    this.addEventListener('mouseleave', dragEnd);
    this.addEventListener('mousemove', drag);
  };

HTMLElement.prototype.onOutsideClick =
  HTMLElement.prototype.onOutsideClick ||
  function (this: HTMLElement, handler: (e: MouseEvent | TouchEvent) => void, otherDependenceElement?: HTMLElement[]) {
    const listener = (event: MouseEvent | TouchEvent) => {
      // Do nothing if clicking ref's element or descendent elements
      if (!event.target || !this || this.contains(event.target as Node)) {
        return;
      }

      if (otherDependenceElement && otherDependenceElement.some((r) => r?.contains(event.target as Node))) {
        return;
      }

      handler(event);
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
  };

(() => {
  const breakpoints = {
    MOBILE: 480,
    TABLET: 768,
    DESKTOP: 1280,
    DESKTOP_LARGE: 1920,
  };

  const viewportType = {
    mobile: 'mobile',
    tablet: 'tablet',
    desktop: 'desktop',
  };
  const mediaQueryChangeEventName = 'mq-matched';

  const mqMobile = window.matchMedia(`only screen and (max-width: ${(breakpoints.TABLET - 0.02).toString()}px)`);
  const mqTablet = window.matchMedia(
    `only screen and (min-width: ${breakpoints.TABLET.toString()}px) and (max-width: ${(breakpoints.DESKTOP - 0.02).toString()}px)`
  );
  const mqDesktop = window.matchMedia(`only screen and (min-width: ${breakpoints.DESKTOP.toString()}px)`);

  mqMobile.addEventListener('change', (e) => {
    if (e.matches)
      window.dispatchEvent(
        new CustomEvent(mediaQueryChangeEventName, {
          detail: {
            viewportType: viewportType.mobile,
          },
        })
      );
  });

  mqTablet.addEventListener('change', (e) => {
    if (e.matches)
      window.dispatchEvent(
        new CustomEvent(mediaQueryChangeEventName, {
          detail: {
            viewportType: viewportType.tablet,
          },
        })
      );
  });

  mqDesktop.addEventListener('change', (e) => {
    if (e.matches)
      window.dispatchEvent(
        new CustomEvent(mediaQueryChangeEventName, {
          detail: {
            viewportType: viewportType.desktop,
          },
        })
      );
  });
})();

window.kfApi =
  window.kfApi ||
  (() => {
    const getAntiForgeryToken = () => {
      const input = document?.querySelector<HTMLInputElement>('input[type=hidden][name="__RequestVerificationToken"]');

      return input?.value;
    };

    const getApiUrl = (remain: string, params?: RequestParams) => {
      const baseApiUrl = import.meta.env.VITE_APP_API_URL ?? window.location.origin;
      const url = new URL(remain, baseApiUrl);

      if (params) {
        Object.keys(params).forEach((key) => {
          if (params[key] !== undefined) {
            url.searchParams.append(key, typeof params[key] === 'boolean' ? (params[key] as boolean).toString() : (params[key] as string));
          }
        });
      }

      return url.toString();
    };

    const sendAsync = async (
      remain: string,
      method: Method,
      params?: RequestParams,
      body?: unknown,
      skipResponseBody?: boolean,
      needCsrf?: boolean
    ) => {
      try {
        const url = getApiUrl(remain, params);

        const requestInit: RequestInit = {
          method: method,
        };

        if (body) {
          requestInit.body = JSON.stringify(body);
          requestInit.headers = {
            'Content-Type': 'application/json',
          };
        }

        if (needCsrf) {
          const antiForgeryToken = getAntiForgeryToken();

          if (antiForgeryToken) {
            requestInit.headers = {
              ...requestInit.headers,
              'X-XSRF-TOKEN': antiForgeryToken,
            };
          }
        }

        return skipResponseBody ? fetch(url, requestInit) : fetch(url, requestInit).then((res) => res.json());
      } catch (error) {
        return Promise.reject(error instanceof Error ? error : new Error(typeof error === 'string' ? error : JSON.stringify(error)));
      }
    };

    const getAsync = async (remain: string, params?: RequestParams) => {
      return sendAsync(remain, Method.GET, params);
    };

    const postAsync = async (remain: string, params?: RequestParams, body?: unknown, skipResponseBody?: boolean) => {
      return sendAsync(remain, Method.POST, params, body, skipResponseBody, true);
    };

    const putAsync = async (remain: string, params?: RequestParams, body?: unknown, skipResponseBody?: boolean) => {
      return sendAsync(remain, Method.PUT, params, body, skipResponseBody, true);
    };

    const patchAsync = async (remain: string, params?: RequestParams, body?: unknown, skipResponseBody?: boolean) => {
      return sendAsync(remain, Method.PATCH, params, body, skipResponseBody, true);
    };

    const deleteAsync = async (remain: string, params?: RequestParams, body?: unknown, skipResponseBody?: boolean) => {
      return sendAsync(remain, Method.DELETE, params, body, skipResponseBody, true);
    };

    return {
      getApiUrl,
      getAsync,
      postAsync,
      putAsync,
      patchAsync,
      deleteAsync,
    };
  })();
